import { Modal } from 'theme';

/**
 * Product highlight popup.
 *
 * The newsletter popup's behaviour — open itself after a delay, optionally only
 * once ever — applied to a product promo. It extends the theme's own Modal, so
 * the backdrop, close button, focus trap, scroll lock and enter/leave
 * animations all come from the base theme rather than being reimplemented.
 *
 * It deliberately keeps its own storage key instead of reusing the newsletter
 * popup's `theme:popup-appeared`: the two sections are configured
 * independently, and a shared key would let whichever opened first silence the
 * other permanently.
 */

const SEEN_KEY = 'theme:product-popup-appeared';

// Storage throws in private mode and when site data is blocked, and can simply
// be absent. The popup has to keep working in those cases rather than take the
// page down, so every access is guarded and a failed read just means "unseen".
function readFlag(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeFlag(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Nothing to do: the popup still works, it just cannot remember.
  }
}

class ProductHighlightPopup extends Modal {
  #timeout = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.shouldAppearAutomatically) {
      this.#timeout = setTimeout(() => this.show(), this.apparitionDelay);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    // The theme editor tears sections down and re-renders them on every change,
    // so a pending timer would otherwise fire against a detached element.
    clearTimeout(this.#timeout);
  }

  get apparitionDelay() {
    return parseInt(this.getAttribute('apparition-delay') || 0) * 1000;
  }

  // The section already sits inside the overlay group, so it must not be moved.
  get shouldAppendToBody() {
    return false;
  }

  get shouldAppearAutomatically() {
    return !(this.hasAttribute('only-once') && readFlag(SEEN_KEY) === 'true');
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);

    if (name === 'open' && this.open) {
      writeFlag(SEEN_KEY, 'true');
    }
  }
}

if (!window.customElements.get('product-highlight-popup')) {
  window.customElements.define('product-highlight-popup', ProductHighlightPopup);
}
